import { useEffect, useState } from "react";
import UserLayout from "@/components/UserLayout";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Copy, CreditCard, ExternalLink } from "lucide-react";

export default function Deposit() {
  const [info, setInfo] = useState(null);
  const [txs, setTxs] = useState([]);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("iban");
  const [reference, setReference] = useState("");
  const [loading, setLoading] = useState(false);
  const [startingId, setStartingId] = useState(null);

  const loadTx = () => api.get("/transactions/my").then((r) => setTxs(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/deposit-info").then((r) => setInfo(r.data)).catch(() => {});
    loadTx();
    const params = new URLSearchParams(window.location.search);
    if (params.get("shopier") === "done") {
      toast.success("Ödeme talebiniz alındı. Bakiyeniz ödeme onaylandıktan sonra otomatik yüklenecek.");
      window.history.replaceState({}, "", "/deposit");
    }
  }, []);

  const copy = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success("Kopyalandı");
  };

  const submit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) return toast.error("Geçerli bir tutar girin");
    setLoading(true);
    try {
      await api.post("/deposit", { amount: amt, method, reference });
      toast.success("Bildiriminiz alındı. Admin onayı bekleniyor.");
      setAmount("");
      setReference("");
      loadTx();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const buyPackage = async (pkg) => {
    setStartingId(pkg.id);
    try {
      const r = await api.post("/deposit/shopier/start", { package_id: pkg.id });
      loadTx();
      toast.success(`₺${pkg.amount} paketi için Shopier ödeme sayfası açılıyor...`);
      // Give the toast a moment to show, then redirect in same tab
      setTimeout(() => {
        window.location.assign(r.data.shopier_url);
      }, 800);
    } catch (err) {
      toast.error(formatApiError(err));
      setStartingId(null);
    }
  };

  const Row = ({ label, value, testid }) => (
    <div className="flex items-center justify-between border-b border-white/5 py-3 last:border-0" data-testid={testid}>
      <div className="text-xs text-white/50 uppercase tracking-[0.15em]">{label}</div>
      <div className="flex items-center gap-2">
        <div className="font-mono text-sm text-white break-all text-right max-w-[280px]">{value || "—"}</div>
        {value && (
          <button onClick={() => copy(value)} className="text-white/40 hover:text-white transition-colors" data-testid={`copy-${testid}`}>
            <Copy className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );

  const packages = info?.shopier?.packages || [];
  const shopierEnabled = info?.shopier?.enabled && packages.length > 0;

  return (
    <UserLayout>
      <div className="mb-10">
        <div className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Ödeme</div>
        <h1 className="font-display text-4xl font-black">Bakiye Yükle</h1>
        <p className="text-white/60 mt-2 text-sm">
          {shopierEnabled
            ? "Aşağıdaki paketlerden birini seçerek kredi kartıyla anında ödeyin veya havale/kripto ile bildirim gönderin."
            : "Aşağıdaki hesap bilgilerine ödeme yapın, ardından form ile bildirin. Admin onayından sonra bakiyeniz güncellenir."}
        </p>
      </div>

      {shopierEnabled && (
        <div className="mb-10" data-testid="shopier-packages-section">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 rounded-full bg-[hsl(43,74%,55%)]/15 border border-[hsl(43,74%,55%)]/40 flex items-center justify-center">
              <CreditCard className="w-4 h-4 text-[hsl(43,74%,65%)]" />
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(43,74%,65%)]">Kredi Kartı ile Yükle</div>
              <div className="text-xs text-white/50">Shopier güvenli ödeme altyapısı — Bakiye onay sonrası anında yüklenir</div>
            </div>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {packages.map((p) => {
              const loading = startingId === p.id;
              return (
                <button
                  key={p.id}
                  onClick={() => buyPackage(p)}
                  disabled={!!startingId}
                  data-testid={`shopier-pkg-btn-${p.id}`}
                  className="group border border-white/10 hover:border-[hsl(43,74%,55%)]/60 bg-gradient-to-br from-[hsl(220,40%,10%)] to-[hsl(220,40%,7%)] hover:from-[hsl(220,40%,12%)] rounded-sm p-4 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 group-hover:text-[hsl(43,74%,65%)]">
                      {p.label}
                    </div>
                    <ExternalLink className="w-3 h-3 text-white/30 group-hover:text-[hsl(43,74%,65%)]" />
                  </div>
                  <div className="font-display text-2xl font-black text-white">
                    ₺{Number(p.amount).toFixed(0)}
                  </div>
                  <div className="text-[10px] text-white/40 mt-2 uppercase tracking-[0.15em]">
                    {loading ? "Yönlendiriliyor…" : "Kart ile Öde"}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="text-[10px] text-white/40 mt-3 leading-relaxed">
            Ödemenizi tamamladıktan sonra bakiyeniz otomatik yüklenir. Otomatik yüklenmezse en geç 10 dk içinde admin manuel onay yapar.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-6">
          <div className="border border-white/10 bg-[hsl(0,0%,7%)] p-6">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">Havale / EFT (IBAN)</div>
            <Row label="Alıcı Ad" value={info?.iban?.name} testid="iban-name" />
            <Row label="IBAN" value={info?.iban?.number} testid="iban-number" />
            <Row label="Banka" value={info?.iban?.bank} testid="iban-bank" />
          </div>

          <div className="border border-white/10 bg-[hsl(0,0%,7%)] p-6">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-4">Kripto Cüzdanlar</div>
            <Row label="USDT (TRX)" value={info?.crypto?.usdt_trc20} testid="crypto-usdt" />
            <Row label="Bitcoin (BTC)" value={info?.crypto?.btc} testid="crypto-btc" />
            <Row label="Ethereum (ETH)" value={info?.crypto?.eth} testid="crypto-eth" />
          </div>
        </div>

        <div className="space-y-6">
          <form onSubmit={submit} className="border border-white/10 bg-[hsl(0,0%,7%)] p-6 space-y-4" data-testid="deposit-form">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Havale / Kripto Bildirimi</div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">Tutar (₺)</label>
              <input
                data-testid="deposit-amount-input"
                type="number"
                step="0.01"
                min="1"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
                className="w-full mt-1 bg-transparent border border-white/15 focus:border-[hsl(221,83%,53%)] outline-none px-3 py-2.5 text-sm rounded-sm"
              />
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">Ödeme Yöntemi</label>
              <select
                data-testid="deposit-method-select"
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="w-full mt-1 bg-transparent border border-white/15 focus:border-[hsl(221,83%,53%)] outline-none px-3 py-2.5 text-sm rounded-sm"
              >
                <option value="iban" className="bg-[hsl(0,0%,7%)]">Havale / EFT (IBAN)</option>
                <option value="crypto" className="bg-[hsl(0,0%,7%)]">Kripto (USDT TRX)</option>
              </select>
            </div>

            <div>
              <label className="text-[10px] uppercase tracking-[0.2em] text-white/50 font-bold">Referans / İşlem Notu</label>
              <input
                data-testid="deposit-reference-input"
                type="text"
                placeholder="Ör: gönderen ad, TX hash..."
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                className="w-full mt-1 bg-transparent border border-white/15 focus:border-[hsl(221,83%,53%)] outline-none px-3 py-2.5 text-sm rounded-sm"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="deposit-submit-btn"
              className="w-full bg-[hsl(221,83%,53%)] hover:bg-[hsl(221,83%,58%)] disabled:opacity-50 text-white font-bold text-sm uppercase tracking-[0.15em] py-3 rounded-sm transition-colors"
            >
              {loading ? "Gönderiliyor…" : "Bildirimi Gönder"}
            </button>
          </form>

          <div className="border border-white/10 bg-[hsl(0,0%,7%)] p-6">
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-3">Son Bildirimlerim</div>
            {txs.length === 0 ? (
              <div className="text-sm text-white/40">Henüz bildiriminiz yok.</div>
            ) : (
              <div className="space-y-2">
                {txs.slice(0, 5).map((t) => (
                  <div key={t.id} className="flex items-center justify-between text-sm border-b border-white/5 last:border-0 py-2">
                    <div>
                      <div className="font-mono text-white">₺{Number(t.amount).toFixed(2)}</div>
                      <div className="text-xs text-white/40 uppercase tracking-[0.15em]">{t.method}</div>
                    </div>
                    <TxStatus s={t.status} />
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </UserLayout>
  );
}

function TxStatus({ s }) {
  const map = {
    0: { label: "Bekliyor", cls: "text-[hsl(38,92%,60%)] border-[hsl(38,92%,60%)]/40" },
    1: { label: "Onaylandı", cls: "text-[hsl(142,71%,55%)] border-[hsl(142,71%,55%)]/40" },
    2: { label: "Reddedildi", cls: "text-red-400 border-red-500/40" },
  };
  const m = map[s] || map[0];
  return (
    <span className={`border px-2 py-0.5 text-[10px] uppercase tracking-[0.15em] font-bold ${m.cls}`}>{m.label}</span>
  );
}
